// The windowed accounting of Umami `/api/send` attempts — the instrument that makes
// "a broken collector" and "a quiet feature" distinguishable.
//
// ⛔ THE DEFECT THIS EXISTS FOR, stated precisely because the obvious version of it is wrong.
// Every Umami send in `routeViewSender.ts` ends `void fetch(...).catch(() => {})`. Three
// hostname shapes shipped in three days and each cost a full detect-and-diagnose cycle, because
// a failed send left **no trace at all**. Two silent paths, and only one of them is the `catch`:
//
//   1. the RESOLVE path — `fetch` RESOLVES for every HTTP status including 400/500
//      (`response.ok === false`), and nothing ever read `response`; and
//   2. the `catch` — for a rejection, which happens on network-level failure.
//
// ⇒ A detector that replaced only the `catch` would have fixed nothing, and that was the
//   headline comment in this module's sibling until 2026-09-18.
//
// ⚠️ "A 4xx RESOLVES" IS A SAME-ORIGIN FACT, NOT A PLATFORM GUARANTEE. Cross-origin it holds only
// while the collector returns `access-control-allow-origin` on ERROR responses — measured on the
// live collector (both probes, non-mutating: `OPTIONS` + `GET /api/send`). Withhold that header
// and a 400 arrives as `TypeError: Failed to fetch`: unreadable, and indistinguishable from a
// dead network. That is why `network` below is defined by what the CLIENT could determine rather
// than by what the server did — see `UMAMI_SEND_WINDOW_NETWORK`.
import { UMAMI_SEND_WINDOW_EVENT_TYPE, UMAMI_SEND_WINDOW_HTTP_4XX, UMAMI_SEND_WINDOW_HTTP_5XX, UMAMI_SEND_WINDOW_NETWORK, UMAMI_SEND_WINDOW_NO_FAILURE, UMAMI_SEND_WINDOW_UNKNOWN, } from './clientTelemetryContract';
/**
 * Window length — and it is a VOLUME decision as much as a detection-latency one.
 *
 * At 120s a session emits at most one window event every two minutes (30/hour), which
 * `CLIENT_EVENT_BUDGET.umami_send_window` is sized to cover fully for any session under ~54 min.
 * This event is the first on the Class-1 plane that fires on SUCCESS — every other type there is
 * failure-only and therefore rare — so its uncapped rate would be one row per session per window,
 * fleet-wide, forever. The window IS the volume bound; the budget is the second one.
 *
 * ⚠️ Detection latency is bounded by whichever comes second: the window elapsing, and the next
 * send. Nothing here runs on a timer, so a session that sends once and idles reports nothing for
 * that window — see the KNOWN TRADE at `onSendAttempted`.
 */
export const UMAMI_SEND_WINDOW_MS = 120_000;
/**
 * How long an attempt may stay un-settled before it is reported as `unobservable`.
 *
 * ⛔ THIS IS NOT A TIMEOUT ON THE REQUEST — nothing is aborted. It is the age at which "we never
 * heard back" stops being "it may still be in flight" and starts being a fact worth reporting.
 *
 * ⚠️ It is the entire reason a healthy system reports `unobservable === 0`, which product's alarm
 * (`sent > 0 && accepted === 0 && unobservable === 0`) depends on. Set it too low and every
 * request still in flight at a window boundary is reported unobservable — which would make the
 * alarm's `unobservable === 0` clause unsatisfiable and hide the condition it exists to catch.
 * A full window is comfortably above any plausible send latency; a realm that dies mid-request
 * (the unload case) is the one that reaches it.
 */
export const UMAMI_SEND_UNOBSERVABLE_GRACE_MS = UMAMI_SEND_WINDOW_MS;
/**
 * Classify a RESOLVED response. The call that matters is the one that is NOT in a `.catch`.
 *
 * `response.ok` is `true` only for 2xx. A 3xx reaches here only if a redirect was not followed,
 * which the default `redirect: 'follow'` makes rare — `unknown` is the honest bucket for it, and
 * for anything else outside 4xx/5xx.
 */
export function classifyUmamiResponse(response) {
    if (response.ok)
        return 'accepted';
    if (response.status >= 400 && response.status < 500)
        return UMAMI_SEND_WINDOW_HTTP_4XX;
    if (response.status >= 500 && response.status < 600)
        return UMAMI_SEND_WINDOW_HTTP_5XX;
    return UMAMI_SEND_WINDOW_UNKNOWN;
}
function defaultElapsedClock() {
    const p = globalThis.performance;
    if (p && typeof p.now === 'function')
        return () => p.now();
    return Date.now;
}
/**
 * ⛔ THE PRECEDENCE USED TO PICK ONE `errorName` FOR A WINDOW THAT HELD SEVERAL FAILURE CLASSES.
 * Architect §6, 2026-09-18. **First present wins** — this is a precedence, NOT a tally.
 *
 *     http-5xx  >  network  >  http-4xx  >  unknown
 *
 * ⛔ DO NOT RE-SORT THIS BY SEVERITY. `http-4xx` ranks BELOW `network` deliberately: a 4xx means
 * the pipeline WORKED and refused the payload, so it is **less diagnostic of a broken collector
 * even though it is louder**. A window holding one 5xx and ten 4xx reports `http-5xx`, because the
 * question the token answers is "what does this tell us about the collector", not "which was most
 * common" — the counts carry the frequency, and they ride unmixed beside the token.
 *
 * ⚠️ Exported because a test asserts it is exactly the contract's token set minus the
 * failure-free token. A hand-listed order is a second declaration of the vocabulary — the drift
 * the contract exists to prevent — so it is not allowed to be quietly short.
 */
export const UMAMI_SEND_FAILURE_CLASS_ORDER = [
    UMAMI_SEND_WINDOW_HTTP_5XX,
    UMAMI_SEND_WINDOW_NETWORK,
    UMAMI_SEND_WINDOW_HTTP_4XX,
    UMAMI_SEND_WINDOW_UNKNOWN,
];
/**
 * Build the per-session accountant.
 *
 * ⛔ WHY THE COUNTS RIDE `errorMessage`, AND WHAT THAT COSTS. The four numbers have to reach the
 * gateway on the Class-1 wire, and the carrier was chosen by measurement, not preference:
 *
 *   - `context` is a CLOSED set of four keys server-side (`clientTelemetryContextKeys` in
 *     `gateway-service`'s `clienttelemetry.go`) — unknown keys are DROPPED, silently, and the
 *     event still records. Four new keys would also land exactly on the 8-key cap, and web's
 *     generator enforces an exact context key set (`requiredAndOptionalKeys(literal.context,
 *     ['route'], ['ability_id'])`), so the carrier would cost a generator change AND a gateway
 *     change to carry open-ended integers.
 *   - `errorMessage` is already on the wire on both sides, free-form, truncated server-side to
 *     256 bytes, and logged VERBATIM in the D4 `client_event` line. The summary below is ~60 bytes
 *     plus the per-class breakdown (~25 — see `failures=`), so roughly 70–90 bytes in practice and
 *     nowhere near the cap.
 *
 * ⛔ THE BREAKDOWN IS NOT OPTIONAL, AND THIS PARAGRAPH USED TO LEAD A READER ASTRAY. It said the
 * per-class counts "ride unmixed beside the token" while `failures` was cleared before `report()`
 * and never emitted at all — so the claim was false and the summary could not distinguish ten 400s
 * from eleven 5xx (product's hold, 2026-09-18). The counts are now in the summary and a test asserts
 * them, which is what makes the sentence true rather than aspirational.
 *
 * ⚠️ The cost is real and is stated rather than hidden: `errorMessage` is UNTYPED, so nothing
 * server-side validates the shape. The mitigation is that the format is produced in exactly the
 * one place below and is asserted by test — converting "a format asserted in prose" into "a
 * format asserted by a test", which is this card's own standard.
 */
export function createUmamiSendWindow(opts) {
    const windowMs = opts.windowMs ?? UMAMI_SEND_WINDOW_MS;
    const graceMs = opts.unobservableGraceMs ?? UMAMI_SEND_UNOBSERVABLE_GRACE_MS;
    const elapsedNow = opts.elapsedNow ?? defaultElapsedClock();
    /** Insertion-ordered, so iteration is oldest-first. Emitted entries are deleted. */
    const epochs = new Map();
    /** The one open epoch. */
    let open = null;
    let nextEpochId = 1;
    // ⛔ EXACT PAIRING, BECAUSE `fetch` GIVES US THE ATTEMPT AT THE CALL SITE. An earlier draft of
    // this fix paired outcomes to attempts by FIFO order, which is a guess: an ack that arrives out
    // of order — or one whose attempt was already reaped as `unobservable` and only THEN settles —
    // consumes the WRONG attempt's slot, so a window can report an outcome for an attempt it never
    // made. Since `sendUmamiPayload` holds a per-call closure, it can carry the attempt itself and no
    // guessing is needed. `resolved` is the guard that makes each attempt contribute exactly once.
    const outstanding = new Set();
    function openEpoch(t) {
        const ep = {
            id: nextEpochId,
            openedAt: t,
            closed: false,
            attempts: 0,
            accepted: 0,
            rejected: 0,
            expired: 0,
            live: 0,
            failures: new Map(),
        };
        nextEpochId += 1;
        epochs.set(ep.id, ep);
        open = ep;
        return ep;
    }
    /**
     * Age out attempts that will never settle.
     *
     * `force` is the TEARDOWN case: the realm is going away, so nothing will settle and every
     * remaining attempt is a fact worth reporting. Without it, only attempts past the grace expire —
     * a request still in flight is not evidence of anything yet.
     *
     * ⛔ MARKING THE ATTEMPT RESOLVED IS THE POINT, not a bookkeeping side effect: it is what makes a
     * late ack for a reaped attempt a no-op instead of a second credit. Without it, the double-count
     * rebuilds itself one level down.
     */
    function reap(now, force) {
        for (const attempt of outstanding) {
            if (force || now - attempt.at >= graceMs) {
                outstanding.delete(attempt);
                attempt.resolved = true;
                const ep = epochs.get(attempt.epochId);
                if (ep !== undefined) {
                    ep.expired += 1;
                    ep.live -= 1;
                }
            }
        }
    }
    function emit(ep) {
        const sent = ep.attempts;
        const accepted = ep.accepted;
        const rejected = ep.rejected;
        const unobservable = ep.expired;
        // A window that accounted for nothing has nothing to say. ⛔ NOTE WHAT IS *NOT* THE TEST:
        // `sent === 0` alone would SKIP an epoch that was credited an outcome without making the
        // attempt — which is precisely the mis-attribution this rewrite exists to surface. All four
        // must be zero before a window is silent.
        if (sent === 0 && accepted === 0 && rejected === 0 && unobservable === 0)
            return;
        let errorName = UMAMI_SEND_WINDOW_NO_FAILURE;
        for (const cls of UMAMI_SEND_FAILURE_CLASS_ORDER) {
            if (ep.failures.has(cls)) {
                errorName = cls;
                break;
            }
        }
        // ⛔ PER-CLASS COUNTS RIDE THE SUMMARY, AND THE DOCBLOCK ABOVE ONLY BECOMES TRUE BECAUSE THEY
        // DO (product's hold, 2026-09-18). `errorName` carries the window's highest-PRECEDENCE class —
        // ONE token, deliberately — and no four-total summary can stand in for the breakdown: ten 400s
        // plus one 503 and eleven 503s both read `sent=11 rejected=11`, with the same precedence token
        // `http-5xx`, and those are different collector states with different owners. The rule "the
        // token says what the window says about the collector, the counts carry the frequency" is only
        // true while both are emitted; `failures` was cleared before `report()` and never shipped.
        const classes = [];
        for (const cls of UMAMI_SEND_FAILURE_CLASS_ORDER) {
            const n = ep.failures.get(cls) ?? 0;
            if (n > 0)
                classes.push(`${cls}:${n}`);
        }
        const summary = `sent=${sent} accepted=${accepted} rejected=${rejected}` +
            ` unobservable=${unobservable} window_ms=${windowMs}` +
            (classes.length > 0 ? ` failures=${classes.join(',')}` : '');
        try {
            const event = {
                eventType: UMAMI_SEND_WINDOW_EVENT_TYPE,
                errorName,
                errorMessage: summary,
            };
            opts.report(event);
        }
        catch {
            /* ⛔ the counter's own failure must not recurse, and must not reach the caller. A failed
               report of a failed send is the obvious trap; it is swallowed deliberately, and it is NOT
               recorded anywhere on this window — the Class-1 plane is a different plane from the Umami
               sends being counted, so a report failure can never inflate `unobservable` here. */
        }
    }
    /**
     * Age out what will never settle, then emit every CLOSED epoch that has nothing left in flight —
     * oldest first, so emission order matches the order the windows actually elapsed.
     *
     * ⛔ AN EPOCH IS EMITTED ONLY WHEN `live === 0`, AND THAT DELAY IS DELIBERATE. Reporting a closed
     * epoch while one of its attempts is still in flight would have to guess, and both guesses are
     * wrong in the expensive direction: call it `unobservable` and a healthy system reports a
     * fabricated failure (which is what makes product's `unobservable === 0` clause unsatisfiable);
     * carry it into the next window and the counts no longer reconcile. So the epoch simply waits.
     *
     * ⚠️ THE COST, stated rather than discovered: an attempt that never settles holds its epoch open
     * until it ages past the grace, so with the defaults (`graceMs === windowMs`) a window whose
     * attempts all settle is emitted at its rollover as before, while one holding a straggler is
     * emitted up to one further window later. Detection latency for a genuinely unanswerable send
     * therefore remains bounded by the grace — it is the grace that defines "unanswerable" — and the
     * normal path is unchanged. Nothing emits on a timer; a session that goes quiet after the
     * straggler still needs one more send to trigger the drain — `flush(true)` would do it, but no
     * shell calls it (see `onSendAttempted`), so in production the trigger is always another send.
     */
    function flush(force) {
        const now = elapsedNow();
        reap(now, force);
        // Teardown closes the open epoch too: nothing further will settle, so it is final.
        if (force && open !== null) {
            open.closed = true;
            open = null;
        }
        // ⛔ EVERY CLOSED, FULLY-ACCOUNTED EPOCH DRAINS — `continue`, NOT `break`, and the difference
        // is a defect I wrote and this suite caught. An epoch holding one never-settling attempt stays
        // non-final for a whole grace; `break` made that ONE straggler hold back **every later window**
        // (head-of-line blocking), so a single hung request could silence the instrument for as long as
        // the grace lasts while the sends behind it were perfectly healthy. A straggler may delay its
        // OWN window's report; it may not delay anyone else's.
        //
        // ⚠️ The accepted cost: summaries can now be emitted out of window order when an earlier epoch
        // is still waiting. Stated rather than hidden — the summary carries no window index, so a
        // reader sees a sequence, not a series, and the numbers remain exact per window.
        // ⛔ `live <= 0`, NOT `live === 0`, AND THE DIFFERENCE IS A SAFETY PROPERTY RATHER THAN A
        // TIDY-UP. On correct accounting `live` never goes below zero, so the two are identical — which
        // is exactly why the strict form is the tempting one. But if the accounting is ever WRONG, `===`
        // turns that anomaly into **an epoch that is never emitted at all**: the window goes SILENT, and
        // a silent instrument is byte-for-byte indistinguishable from a healthy one. That is the failure
        // this entire module exists to abolish, so the gate must not be able to produce it. Emitting on
        // `<= 0` instead surfaces the anomaly *in the numbers* (the conservation identity will fail,
        // because `sent` is counted independently) where a reader can see it.
        //
        // ⚠️ AND SAY WHAT THAT COSTS, so the next reader does not assume a test holds it: **this branch
        // is not independently pinnable.** On correct accounting `live` never goes negative, so `> 0`
        // and `!== 0` are equivalent for EVERY reachable input — a mutation between them is green
        // (measured, 2026-09-18). It differs only in combination with another accounting defect, which
        // is the case it is insurance against. Deliberate, and unpinnable rather than untested.
        const emitted = [];
        for (const [id, ep] of epochs) {
            if (!ep.closed || ep.live > 0)
                continue;
            emit(ep);
            emitted.push(id);
        }
        for (const id of emitted)
            epochs.delete(id);
    }
    return {
        onSendAttempted() {
            const t = elapsedNow();
            // Lazy rollover: a window closes when traffic arrives after it has elapsed. No timer, so
            // there is no teardown and no wall-clock flake under test.
            // ⛔ KNOWN TRADE, AND IT IS LARGER THAN "THE LAST WINDOW" — corrected 2026-09-18 (reviewer,
            // `web!650`). The sentence here used to say `flush()` "exists for a shell that can call it on
            // teardown", which reads as a mitigation that is in place. **IT IS NOT: no shell flushes.**
            // The window is created in three places (`RouteViewReporter.tsx`, `ApiProvider.tsx`,
            // `miniappTguidMissingSender.ts`) and `flush()` is called from none of them — verified by
            // grep across the repo. So the mitigation does not exist and must not be read as existing.
            //
            // ⚠️ AND THE LOSS IS NOT CONFINED TO "THE LAST WINDOW". For any session SHORTER THAN ONE
            // WINDOW, the last window is the ONLY window — so it emits **nothing at all, ever**, and a
            // session that made three failing sends reports byte-for-byte what a session that made none
            // reports. That is the population `umamiSendWindow.test.ts:165-166` refuses to accept ("a failure-only emitter could not
            // distinguish 'healthy' from 'detector dead', and both would be silent") and the shipped
            // wiring leaves it outside the instrument. Tracked as a follow-up rather than fixed here:
            // wiring a teardown hook is a behaviour change across three send sites and is NOT a tweak to
            // this module — `clientEvent.ts:58-63` rejects the obvious hook (`pagehide` produces two
            // round trips with only the second protected) and unload coverage is a different design.
            // The summary carries `window_ms`, so a reader can tell a short window from a missing one —
            // which is the only mitigation that actually ships.
            if (open !== null && t - open.openedAt >= windowMs) {
                open.closed = true;
                open = null;
                // ⛔ NOT forced: attempts still in flight here may yet settle, and this epoch will be
                // emitted by whichever later drain finds it `live === 0`.
                flush(false);
            }
            const ep = open ?? openEpoch(t);
            ep.attempts += 1;
            ep.live += 1;
            const attempt = { at: t, epochId: ep.id, resolved: false };
            outstanding.add(attempt);
            return attempt;
        },
        onSendOutcome(attempt, outcome) {
            // ⛔ THE FIX: credited to the epoch THIS ATTEMPT belonged to, never to whichever epoch is
            // open when the ack lands. Crediting the open epoch is what let a stale ack inflate a window
            // that sent nothing and so report a broken collector healthy.
            //
            // Each attempt contributes EXACTLY ONCE. A second outcome, or an outcome for an attempt
            // already reaped as `unobservable`, is DROPPED — re-crediting it would be the double-count,
            // and the epoch it belonged to may already have been emitted and cannot be retracted.
            if (attempt.resolved)
                return;
            attempt.resolved = true;
            outstanding.delete(attempt);
            const ep = epochs.get(attempt.epochId);
            if (ep === undefined)
                return; // already emitted — nothing left to credit
            ep.live -= 1;
            if (outcome === 'accepted') {
                ep.accepted += 1;
                return;
            }
            ep.rejected += 1;
            ep.failures.set(outcome, (ep.failures.get(outcome) ?? 0) + 1);
        },
        flush() {
            // Teardown: force, so in-flight attempts are reported rather than silently dropped.
            flush(true);
        },
    };
}
/**
 * Issue one `/api/send` and report its outcome — the ONLY place a `/api/send` response is read.
 *
 * ⛔ ONE PLACE, NOT THREE. The three senders in `routeViewSender.ts` are three copies of the same
 * fetch shape; leaving the response read at each of them would mean a site that forgets it
 * reports every send as `unobservable` (visible, but by accident), and a site that reads it
 * differently reports a different world. This helper makes that drift unrepresentable.
 *
 * ⛔ `fetch` THROWS SYNCHRONOUSLY on a malformed URL, BEFORE it returns a promise — a rejection
 * `.catch` cannot see that. The attempt is already counted by then, so the outcome is recorded
 * here too; otherwise the send would land in `unobservable` by accident rather than by fact.
 */
export function sendUmamiPayload(fetchImpl, url, init, window) {
    const attempt = window.onSendAttempted();
    try {
        void fetchImpl(url, init)
            .then((response) => window.onSendOutcome(attempt, classifyUmamiResponse(response)))
            .catch(() => window.onSendOutcome(attempt, UMAMI_SEND_WINDOW_NETWORK));
    }
    catch {
        window.onSendOutcome(attempt, UMAMI_SEND_WINDOW_NETWORK);
    }
}
//# sourceMappingURL=umamiSendWindow.js.map